import { useMemo, useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SeeItInAction } from "@/components/SeeItInAction";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { priorityMeta, statusMeta, phaseColor } from "@/lib/constants";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { Sparkles, Loader2, ClipboardCheck, Copy, ShieldAlert, ClipboardList, BarChart3 } from "lucide-react";
import { HEALTH_DOT, actionHealth, riskHealth, decisionHealth, originOf, SEVERITY_BANDS } from "@/lib/health";
import { CHART_DEFS, ReportChart } from "@/components/ReportCharts";
import { CollapseGroup } from "@/components/ReportExtras";
import { Scale } from "lucide-react";
import { isBefore, parseISO, addDays } from "date-fns";

export default function MeetingPrep({ projectId, tasks, phaseColors }) {
  const colorOf = (p) => (phaseColors && phaseColors[p]) || phaseColor(p);
  const [dueFilter, setDueFilter] = useState("all");
  const [prioFilter, setPrioFilter] = useState("all");
  const [selected, setSelected] = useState({});
  const [meetingType, setMeetingType] = useState("Weekly status");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [risks, setRisks] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [selRisks, setSelRisks] = useState({});
  const [selActions, setSelActions] = useState({});
  const [cp, setCp] = useState(null);
  const [selCharts, setSelCharts] = useState({});
  const activeCharts = useMemo(() => CHART_DEFS.filter((c) => selCharts[c.key]), [selCharts]);
  const [decisions, setDecisions] = useState([]);
  const [selDecisions, setSelDecisions] = useState({});

  useEffect(() => {
    api.get(`/projects/${projectId}/risks`).then(({ data }) => setRisks(data)).catch(() => {});
    api.get(`/projects/${projectId}/action-items`).then(({ data }) => setActionItems(data.filter((a) => !a.archived))).catch(() => {});
    api.get(`/projects/${projectId}/critical-path`).then(({ data }) => setCp(data)).catch(() => {});
    api.get(`/projects/${projectId}/decisions`).then(({ data }) => setDecisions(data)).catch(() => {});
  }, [projectId]);

  const filtered = useMemo(() => {
    const now = new Date();
    return tasks.filter((t) => {
      if (prioFilter !== "all" && t.priority !== prioFilter) return false;
      if (dueFilter !== "all") {
        if (!t.end_date) return false;
        const end = parseISO(t.end_date);
        if (dueFilter === "overdue" && !(isBefore(end, now) && t.status !== "done")) return false;
        if (dueFilter === "week" && !isBefore(end, addDays(now, 7))) return false;
      }
      return true;
    });
  }, [tasks, dueFilter, prioFilter]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectAll = () => {
    const all = {};
    filtered.forEach((t) => (all[t.id] = true));
    setSelected(all);
  };

  const generate = async () => {
    setLoading(true);
    setContent("");
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/meeting`, {
        task_ids: selectedIds.length ? selectedIds : filtered.map((t) => t.id),
        meeting_type: meetingType,
        risk_ids: Object.keys(selRisks).filter((k) => selRisks[k]),
        action_item_ids: Object.keys(selActions).filter((k) => selActions[k]),
        decision_ids: Object.keys(selDecisions).filter((k) => selDecisions[k]),
        include_blockers: true,
      });
      setContent(data.content);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to generate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="meeting-prep">
      <div className="border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3"><h3 className="font-display text-lg font-bold">Pull tasks</h3><SeeItInAction chapter={12} /></div>
          <button onClick={selectAll} className="text-xs text-primary font-semibold hover:underline" data-testid="select-all-tasks">Select all ({filtered.length})</button>
        </div>
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={dueFilter} onValueChange={setDueFilter}>
            <SelectTrigger className="w-[150px] h-9" data-testid="due-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All due dates</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="week">Due this week</SelectItem>
            </SelectContent>
          </Select>
          <Select value={prioFilter} onValueChange={setPrioFilter}>
            <SelectTrigger className="w-[150px] h-9" data-testid="priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {filtered.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No tasks match these filters.</p>}
          {filtered.map((t) => {
            const pm = priorityMeta(t.priority);
            const sm = statusMeta(t.status);
            return (
              <label key={t.id} className="flex items-start gap-3 border border-border rounded-md p-3 cursor-pointer hover:border-foreground/30 transition-colors" data-testid={`meeting-task-${t.id}`}>
                <Checkbox checked={!!selected[t.id]} onCheckedChange={() => toggle(t.id)} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{t.title}</p>
                  <div className="flex items-center flex-wrap gap-2 mt-1.5 text-[11px]">
                    <span style={{ color: colorOf(t.phase) }} className="font-semibold">{t.phase}</span>
                    <span style={{ color: pm.color }} className="font-semibold">{pm.label}</span>
                    <span style={{ color: sm.color }} className="font-semibold">{sm.label}</span>
                    {t.end_date && <span className="text-muted-foreground">due {t.end_date}</span>}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Risks & blockers */}
        <div className="mt-5 border-t border-border pt-4">
          <h4 className="text-sm font-bold flex items-center gap-1.5 mb-2"><ShieldAlert className="h-4 w-4 text-primary" /> Risks & issues to raise</h4>
          {risks.length === 0 ? (
            <p className="text-xs text-muted-foreground">No risks logged.</p>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {(() => {
                const riskRow = (r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer border border-border rounded-md px-2.5 py-1.5 hover:border-foreground/30" data-testid={`meeting-risk-${r.id}`}>
                    <Checkbox checked={!!selRisks[r.id]} onCheckedChange={() => setSelRisks((s) => ({ ...s, [r.id]: !s[r.id] }))} />
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: HEALTH_DOT[riskHealth(r)] }} title={riskHealth(r)} />
                    <span className={`truncate flex-1 ${r.status === "closed" ? "line-through text-muted-foreground" : ""}`}>{r.title}{originOf(r) && <span className="text-[10px] text-muted-foreground ml-1.5">({originOf(r)})</span>}</span>
                    <span className="text-[10px] font-bold text-white px-1.5 rounded" style={{ background: r.status === "closed" ? "#9CA3AF" : r.score >= 15 ? "#EF4444" : r.score >= 10 ? "#F97316" : r.score >= 5 ? "#F59E0B" : "#10B981" }}>{r.score}</span>
                  </label>
                );
                const inProg = risks.filter((r) => r.status !== "closed");
                const closed = risks.filter((r) => r.status === "closed");
                return (
                  <>
                    {inProg.length > 0 && (
                      <CollapseGroup id="meeting-risks-in-progress" testid="meeting-risks-label-in-progress" label="In Progress" color="#10B981" count={inProg.length}>
                        {SEVERITY_BANDS.map(([lb, col, test]) => {
                          const g = inProg.filter((r) => test(r.score));
                          return g.length === 0 ? null : (
                            <div className="ml-3" key={lb}>
                              <CollapseGroup id={`meeting-risks-${lb}`} testid={`meeting-risks-sev-${lb.toLowerCase()}`} label={lb} color={col} count={g.length}>
                                <div className="space-y-1.5">{g.map(riskRow)}</div>
                              </CollapseGroup>
                            </div>
                          );
                        })}
                      </CollapseGroup>
                    )}
                    {closed.length > 0 && (
                      <CollapseGroup id="meeting-risks-closed" testid="meeting-risks-label-closed" label="Closed" color="#2563EB" count={closed.length} defaultOpen={false}>
                        <div className="space-y-1.5">{closed.map(riskRow)}</div>
                      </CollapseGroup>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Decision points */}
        <div className="mt-4 border-t border-border pt-4">
          <h4 className="text-sm font-bold flex items-center gap-1.5 mb-2"><Scale className="h-4 w-4 text-primary" /> Decision points to review</h4>
          {decisions.length === 0 ? (
            <p className="text-xs text-muted-foreground">No decision points logged.</p>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {(() => {
                const decRow = (d) => (
                  <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer border border-border rounded-md px-2.5 py-1.5 hover:border-foreground/30" data-testid={`meeting-decision-${d.id}`}>
                    <Checkbox checked={!!selDecisions[d.id]} onCheckedChange={() => setSelDecisions((s) => ({ ...s, [d.id]: !s[d.id] }))} />
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: HEALTH_DOT[decisionHealth(d)] }} title={decisionHealth(d)} />
                    <span className={`truncate flex-1 ${d.status === "decided" ? "line-through text-muted-foreground" : ""}`}>{d.title}{originOf(d) && <span className="text-[10px] text-muted-foreground ml-1.5">({originOf(d)})</span>}</span>
                    {d.blocking && d.status !== "decided" && <span className="text-[10px] font-bold text-red-500">blocking</span>}
                  </label>
                );
                const inProg = decisions.filter((d) => d.status !== "decided");
                const closed = decisions.filter((d) => d.status === "decided");
                const bands = [["Blocking", "#EF4444", (d) => !!d.blocking], ["Non-blocking", "#10B981", (d) => !d.blocking]];
                return (
                  <>
                    {inProg.length > 0 && (
                      <CollapseGroup id="meeting-decisions-in-progress" testid="meeting-decisions-label-in-progress" label="In Progress" color="#10B981" count={inProg.length}>
                        {bands.map(([lb, col, test]) => {
                          const g = inProg.filter(test);
                          return g.length === 0 ? null : (
                            <div className="ml-3" key={lb}>
                              <CollapseGroup id={`meeting-decisions-${lb}`} testid={`meeting-decisions-cat-${lb.toLowerCase()}`} label={lb} color={col} count={g.length}>
                                <div className="space-y-1.5">{g.map(decRow)}</div>
                              </CollapseGroup>
                            </div>
                          );
                        })}
                      </CollapseGroup>
                    )}
                    {closed.length > 0 && (
                      <CollapseGroup id="meeting-decisions-closed" testid="meeting-decisions-label-closed" label="Closed" color="#2563EB" count={closed.length} defaultOpen={false}>
                        <div className="space-y-1.5">{closed.map(decRow)}</div>
                      </CollapseGroup>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Action items */}
        <div className="mt-4 border-t border-border pt-4">
          <h4 className="text-sm font-bold flex items-center gap-1.5 mb-2"><ClipboardList className="h-4 w-4 text-primary" /> Action items to review</h4>
          {actionItems.length === 0 ? (
            <p className="text-xs text-muted-foreground">No action items.</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {[["In Progress", "#10B981", actionItems.filter((a) => a.status !== "done")], ["Closed", "#2563EB", actionItems.filter((a) => a.status === "done")]].map(([label, color, group]) => group.length === 0 ? null : (
                <CollapseGroup key={label} id={`meeting-actions-${label}`} testid={`meeting-actions-label-${label.toLowerCase().replace(" ", "-")}`} label={label} color={color} count={group.length} defaultOpen={label !== "Closed"}>
                  <div className="space-y-1.5">
                    {group.map((a) => (
                      <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer border border-border rounded-md px-2.5 py-1.5 hover:border-foreground/30" data-testid={`meeting-action-${a.id}`}>
                        <Checkbox checked={!!selActions[a.id]} onCheckedChange={() => setSelActions((s) => ({ ...s, [a.id]: !s[a.id] }))} />
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: HEALTH_DOT[actionHealth(a)] }} title={actionHealth(a)} />
                        {a.display_id && <span className="font-mono text-[10px] font-bold text-primary shrink-0">{a.display_id}</span>}
                        <span className={`truncate flex-1 ${a.status === "done" ? "line-through text-muted-foreground" : ""}`}>{a.title}{originOf(a) && <span className="text-[10px] text-muted-foreground ml-1.5">({originOf(a)})</span>}</span>
                        {a.bucket === "late" && <span className="text-[10px] font-bold text-red-500">late</span>}
                      </label>
                    ))}
                  </div>
                </CollapseGroup>
              ))}
            </div>
          )}
        </div>

        {/* Graphs to present */}
        <div className="mt-4 border-t border-border pt-4">
          <h4 className="text-sm font-bold flex items-center gap-1.5 mb-2"><BarChart3 className="h-4 w-4 text-primary" /> Graphs to present <span className="text-xs text-muted-foreground font-normal">(from Reports)</span></h4>
          <div className="grid grid-cols-2 gap-1.5">
            {CHART_DEFS.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer border border-border rounded-md px-2.5 py-1.5 hover:border-foreground/30" data-testid={`meeting-chart-pick-${c.key}`}>
                <Checkbox checked={!!selCharts[c.key]} onCheckedChange={() => setSelCharts((s) => ({ ...s, [c.key]: !s[c.key] }))} />
                <span className="truncate">{c.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-border rounded-lg p-5 flex flex-col">
        <div className="flex items-center justify-between mb-4 gap-2">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" /> Meeting brief</h3>
          <Select value={meetingType} onValueChange={setMeetingType}>
            <SelectTrigger className="w-[160px] h-9" data-testid="meeting-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Weekly status">Weekly status</SelectItem>
              <SelectItem value="Stakeholder review">Stakeholder review</SelectItem>
              <SelectItem value="Sprint planning">Sprint planning</SelectItem>
              <SelectItem value="Risk review">Risk review</SelectItem>
              <SelectItem value="Kickoff">Kickoff</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={generate} disabled={loading || filtered.length === 0} className="gap-2 mb-4" data-testid="generate-meeting-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Generate agenda &amp; next steps {selectedIds.length ? `(${selectedIds.length})` : ""}
        </Button>

        <div className="flex-1 overflow-y-auto">
          {content ? (
            <div>
              <div className="flex justify-end mb-2">
                <button onClick={() => { navigator.clipboard.writeText(content); toast.success("Copied"); }} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="copy-meeting-button">
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <Markdown text={content} />
            </div>
          ) : activeCharts.length ? null : (
            <div className="h-full flex items-center justify-center text-center text-sm text-muted-foreground px-6 py-10">
              Select tasks and generate a structured agenda, discussion points, and action items powered by AI.
            </div>
          )}
          {activeCharts.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="meeting-charts">
              {activeCharts.map((c) => (
                <ReportChart key={c.key} kind={c.key} tasks={tasks} cpTasks={cp?.tasks || []} risks={risks} actions={actionItems} phaseColors={phaseColors} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
